import { ClientResponse, ClientResponseError } from "@/Common/Api/ClientResponse";
import { Guid } from "@/Common/Models/Guid";
import { FlowsheetClient } from "@/Flowsheets/Api/FlowsheetClient";
import { deleteFlowsheetStoreAction } from "../Actions/Actions";
import { useCommand, UseCommandOptions, UseCommandResult } from "./useCommand";
import { useDispatch } from "./useDispatch";

type DeleteFlowsheetParams = {
    flowsheetId: Guid;
    encounterId: Guid;
};

/**
 * Interface representing the result of the useDeleteFlowsheet hook.
 */
interface UseDeleteFlowsheetResult extends UseCommandResult<ClientResponse<void>, unknown, DeleteFlowsheetParams> {
    /**
     * Deletes a flowsheet by its UID.
     * @param params - The parameters for deleting the flowsheet.
     * @param params.flowsheetId - The ID of the flowsheet to delete.
     * @param params.encounterId - The ID of the encounter associated with the flowsheet.
     * @returns A promise that resolves when the flowsheet is deleted.
     */
    deleteFlowsheet: (params: DeleteFlowsheetParams) => Promise<void>;
}

/**
 * Interface representing the options for the useDeleteFlowsheet hook.
 */
interface UseDeleteFlowsheetOptions extends UseCommandOptions<ClientResponse<void>, ClientResponseError> {
}

/**
 * Custom hook to delete a flowsheet.
 * @param useDeleteFlowsheetOptions - Options for the useDeleteFlowsheet hook.
 * @returns An object containing the deleteFlowsheet function and the mutation result.
 */
function useDeleteFlowsheet(useDeleteFlowsheetOptions?: UseDeleteFlowsheetOptions): UseDeleteFlowsheetResult {
    const dispatch = useDispatch();

    async function command(params: DeleteFlowsheetParams): Promise<ClientResponse<void>> {
        const response = await FlowsheetClient.deleteFlowsheet(params.flowsheetId);

        response.throwIfError();

        dispatch(deleteFlowsheetStoreAction(params));

        return response;
    }

    const mutation = useCommand(command, useDeleteFlowsheetOptions);

    return {
        ...mutation,
        deleteFlowsheet: mutation.mutate
    };
}

export { useDeleteFlowsheet };
